import React from 'react';
import { StickyFooter } from '@ajat/design-system/Configurator';
import { ComponentPage, DemoCard } from '../../shared';

export const meta = {
  component: StickyFooter,
  title: 'Sticky Footer',
  group: 'Configurator',
  importPath: '@ajat/design-system/Configurator',
  figmaNode: '3803:17976',
  description:
    'The product page action bar — total, delivery note and the add-to-cart CTA. Sticky at the bottom on desktop; on mobile the page pins it with `position: fixed; bottom: var(--space-ui-small-m)` and the delivery block drops out. Never use env(safe-area-inset-bottom) here: Safari\'s collapsing address bar makes it jump.',
  props: [
    ['price', 'string', "'289,-'", 'Total, rendered through Price type="editorial"'],
    ['priceLabel', 'string', "'Total'", 'Label above the price'],
    ['priceError', 'bool', 'false', 'Error styling on the price — used by the price-locked order edit'],
    ['targetText', 'string', 'null', 'Target price note shown when the recomputed total must match an existing order'],
    ['deliveryTime', 'string', "'3 Weeks'", 'Delivery time'],
    ['deliveryPrice', 'string', "'Free'", 'Delivery cost'],
    ['deliveryText', 'string', '—', 'Delivery description'],
    ['deliveryMessage', 'string', 'null', 'Replaces the delivery block with a single message'],
    ['showDelivery', 'bool', 'true', 'Show the delivery block (desktop only in practice)'],
    ['buttonLabel', 'string', "'Add to Cart'", 'CTA label — switch to "Update" in edit mode'],
    ['buttonVariant', 'string', "'alternative'", 'Button variant'],
    ['buttonLoading', 'bool', 'false', 'CTA spinner'],
    ['buttonDisabled', 'bool', 'false', 'Disables the CTA — e.g. a sold-out combination'],
    ['onButtonClick', 'func', '—', 'CTA handler'],
    ['className', 'string', "''", 'Additional CSS class'],
  ],
  usage: [
    {
      title: 'On a product page',
      code: `import { StickyFooter } from '@ajat/design-system/Configurator';

<StickyFooter
  className="pp__footer"
  price={\`\${total}\${priceSuffix}\`}
  priceLabel="Total"
  deliveryTime="3 uger"
  deliveryPrice="Gratis"
  deliveryText="Levering til din skole"
  buttonLabel={editOrder ? 'Update' : 'Læg i kurv'}
  buttonDisabled={selectionSoldOut}
  buttonLoading={adding}
  onButtonClick={handleAddToCart}
/>`,
    },
  ],
};

export default function StickyFooterPage() {
  return (
    <ComponentPage meta={meta}>
      <DemoCard title="Desktop" hint="full bar with delivery info" flush>
        <StickyFooter
          price="289,-"
          priceLabel="Total"
          deliveryTime="3 Weeks"
          deliveryPrice="Free"
          deliveryText="Delivery to your school"
          buttonLabel="Add to Cart"
          className="cfg-sticky-footer--preview-desktop"
        />
      </DemoCard>

      <DemoCard title="Mobile" hint="375px — compact floating bar, delivery hidden" surface={false}>
        <div
          className="cfg-sticky-footer-mobile-preview bg-[var(--color-gray-0)] p-[var(--space-ui-large-s)] rounded-[var(--radius-card)] border border-[var(--color-gray-1)] flex items-center justify-center"
          style={{ maxWidth: 375 }}
        >
          <StickyFooter price="289,-" priceLabel="Total" buttonLabel="Add to Cart" />
        </div>
      </DemoCard>

      <DemoCard title="Without delivery" flush>
        <StickyFooter
          price="499,-"
          priceLabel="Total"
          showDelivery={false}
          buttonLabel="Checkout"
          className="cfg-sticky-footer--preview-desktop"
        />
      </DemoCard>

      <DemoCard title="Loading" flush>
        <StickyFooter
          price="289,-"
          priceLabel="Total"
          deliveryTime="3 Weeks"
          deliveryPrice="Free"
          deliveryText="Delivery to your school"
          buttonLabel="Add to Cart"
          buttonLoading
          className="cfg-sticky-footer--preview-desktop"
        />
      </DemoCard>

      <DemoCard title="Disabled — sold out" flush>
        <StickyFooter
          price="289,-"
          priceLabel="Total"
          showDelivery={false}
          buttonLabel="Udsolgt"
          buttonDisabled
          className="cfg-sticky-footer--preview-desktop"
        />
      </DemoCard>
    </ComponentPage>
  );
}
